import { useEffect, PropsWithChildren } from "react";
import { useAppDispatch, useAppState } from "hooks/AppContext";
import { useSessionStorage } from "hooks/useSessionStorage";
import { UserActionTypes } from "interfaces/context/appContextInterfaces";
import { getUser } from "utils/api";
import { formatLog } from "utils/logging";
import { userTrack } from "utils/tracking";
import { getCarmenData } from "utils/location";
import mockUser from "mocks/mockUser.json";
import { User } from "@themuse/design-system";

const setUserCarmenData = async (setUserLocation: any) => {
    try {
        const carmenResponse = await getCarmenData();
        if (carmenResponse) {
            setUserLocation(JSON.stringify(carmenResponse));
        }
    } catch (error) {
        const msg = `Error requesting the location from Carmen at ${error?.request?.path}.`;
        const err = JSON.stringify(formatLog({ error }));
        console.warn(msg, err);
    }
};

const UserInit = (props: PropsWithChildren<{}>) => {
    const { children } = props;
    const { user } = useAppState();
    const [userLocation, setUserLocation] = useSessionStorage("user_location");
    const appDispatch = useAppDispatch();

    useEffect(() => {
        const setUser = async () => {
            try {
                let apiUser: User;
                // User only works in production and staging.
                if (!["www.themuse.com", "www.staging-themuse.com"].includes(window.location.host)) {
                    apiUser = mockUser;
                } else {
                    apiUser = await getUser();
                }

                if (apiUser?.logged_in) {
                    appDispatch({
                        type: UserActionTypes.updateUser,
                        payload: {
                            ...apiUser,
                            location: userLocation ? JSON.parse(userLocation) : null
                        }
                    });
                } else {
                    appDispatch({ type: UserActionTypes.resetUser });
                }
            } catch (error) {
                const msg = `Error requesting the user at ${error?.request?.path}.`;
                const err = JSON.stringify(formatLog({ error }));
                console.warn(msg, err);
            }
        };

        setUser().then(() => {
            // NOTE: An email with `themuse.com` does not mean the user is an admin
            const isMuser = user?.email && user.email.split("@")[1] === "themuse.com";
            userTrack({
                signed_in: user.logged_in ?? false,
                is_muser: isMuser,
                is_admin: isMuser,
                user_id: user.id ?? null
            });
        })
            .catch((error) => {
                userTrack({
                    signed_in: false,
                    is_muser: false,
                    is_admin: false,
                    user_id: null
                });
                const msg = `Error setting the user at ${error?.request?.path}.`;
                const err = JSON.stringify(formatLog({ error }));
                console.error(msg, err);
            });
    }, []);

    useEffect(() => {
        setUserCarmenData(setUserLocation);
    }, []);

    useEffect(() => {
        appDispatch({
            type: UserActionTypes.updateUser,
            payload: {
                ...user,
                location: userLocation ? JSON.parse(userLocation) : null
            }
        });
    }, [userLocation]);

    useEffect(() => {
        // NOTE: An email with `themuse.com` does not mean the user is an admin
        const isMuser = user?.email && user.email.split("@")[1] === "themuse.com";
        userTrack({
            signed_in: user?.logged_in ?? false,
            is_muser: isMuser,
            is_admin: isMuser,
            user_id: user?.id ?? null
        });
    }, [user]);

    return <>{children}</>;
};
UserInit.displayName = "UserInit";

export { UserInit };
